import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Row,
  Col,
  Container,
  Badge,
  ButtonGroup,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  useFetchUserMolesQuery,
  useFetchBodyPartsQuery,
  useFetchAllMoleImagesQuery,
} from '../../store/apis/skinMolesApi';
import {
  InteractiveBodySVG,
  MoleImageCarouselModal,
  MoleCard,
  MoleCreationModal,
} from './components';
import { getBodyPartName } from './utils';
import './SkinMolesPage.css';

/**
 * Main skin moles management component
 * Displays interactive body diagram and mole management interface
 */
const SkinMolesComponent = () => {
  const { t, i18n } = useTranslation();

  // Modal state for image carousel
  const [showCarouselModal, setShowCarouselModal] = useState(false);
  const [selectedMole, setSelectedMole] = useState(null);
  const [selectedMoleImages, setSelectedMoleImages] = useState([]);
  const [selectedMoleBodyPartName, setSelectedMoleBodyPartName] = useState(null);

  // Get authentication state
  const { userToken } = useSelector((state) => state.auth);

  // Create mode state
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createBodyPart, setCreateBodyPart] = useState(null);

  // Modal trigger function
  const handleShowCarousel = (mole, images) => {
    // Get the translated body part name for this mole
    const translatedBodyPartName =
      mole.body_part && mole.body_part !== 'None' && mole.body_part !== null
        ? getDisplayBodyPartName(mole.body_part)
        : null;

    setSelectedMole(mole);
    setSelectedMoleImages(images);
    setShowCarouselModal(true);

    // Store the translated body part name for the modal
    setSelectedMoleBodyPartName(translatedBodyPartName);
  };

  const handleHideCarousel = () => {
    setShowCarouselModal(false);
    setSelectedMole(null);
    setSelectedMoleImages([]);
    setSelectedMoleBodyPartName(null);
  };

  // Create mode handlers
  const handleToggleCreateMode = () => {
    setIsCreateMode(!isCreateMode);
    setSelectedBodyPart(null); // Clear selection when switching modes
  };

  const handleBodyPartCreateClick = (bodyPartCode) => {
    if (isCreateMode) {
      setCreateBodyPart(bodyPartCode);
      setShowCreateModal(true);
    } else {
      // Normal view mode behavior
      setSelectedBodyPart(bodyPartCode);
    }
  };

  const handleHideCreateModal = () => {
    setShowCreateModal(false);
    setCreateBodyPart(null);
  };

  const handleClearCreateBodyPart = () => {
    setCreateBodyPart(null);
  };

  const handleMoleCreated = async (result) => {
    // Force immediate data refresh
    await Promise.all([refetchUserMoles(), refetchMoleImages()]);
    // Trigger component re-render with fresh data
    setRefreshTrigger(prev => prev + 1);

    // Exit create mode after successful creation
    setIsCreateMode(false);
    setSelectedBodyPart(null);
  };

  const handleMoleDeleted = async () => {
    // Force immediate data refresh after mole deletion
    await Promise.all([refetchUserMoles(), refetchMoleImages()]);
    // Trigger component re-render with fresh data
    setRefreshTrigger(prev => prev + 1);
  };

  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [viewType, setViewType] = useState('front');
  const [forceRender, setForceRender] = useState(false);
  const [imageLoadingTimeout, setImageLoadingTimeout] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const currentLang = i18n.language;
  const isPolish = String(currentLang || '')
    .toLowerCase()
    .startsWith('pl');

  // Force render after timeout to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceRender(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // API calls - include refresh trigger to force new cache keys
  // Also include userToken to ensure cache invalidation on auth change
  // Set a high limit to fetch all user moles (default backend limit might be 5)
  const userMolesParams = { 
    refresh: refreshTrigger, 
    authToken: userToken,
    limit: 100, // High limit to ensure we get all moles
    offset: 0   // Start from beginning
  };

  const {
    data: userMolesData,
    isLoading: molesLoading,
    error: molesError,
    refetch: refetchUserMoles,
  } = useFetchUserMolesQuery(userMolesParams, {
    skip: !userToken, // Skip if no userToken
    refetchOnMountOrArgChange: true,
  });

  // Fetch body parts for both orientations as originally intended
  const frontParams = { orientation: 'front', language: isPolish ? 'PL' : 'EN' };
  const backParams = { orientation: 'back', language: isPolish ? 'PL' : 'EN' };

  const { data: frontBodyPartsData, isLoading: frontBodyPartsLoading } = useFetchBodyPartsQuery(
    frontParams,
    {
      skip: !userToken, // Skip if no userToken
      refetchOnMountOrArgChange: true, // Refetch when language changes
      forceRefetch: true, // Force new request even if we have cached data
    },
  );

  const { data: backBodyPartsData, isLoading: backBodyPartsLoading } = useFetchBodyPartsQuery(
    backParams,
    {
      skip: !userToken, // Skip if no userToken
      refetchOnMountOrArgChange: true, // Refetch when language changes
      forceRefetch: true, // Force new request even if we have cached data
      pollingInterval: undefined,
    },
  );

  // Fetch all mole images with explicit parameters - include refresh trigger
  // Also include userToken to ensure cache invalidation on auth change
  // Set a high limit to fetch all mole images
  const moleImagesParams = { 
    refresh: refreshTrigger, 
    authToken: userToken,
    limit: 1000, // High limit to ensure we get all images
    offset: 0    // Start from beginning
  };

  const {
    data: allMoleImagesData,
    isLoading: imagesLoading,
    error: imagesError,
    refetch: refetchMoleImages,
  } = useFetchAllMoleImagesQuery(moleImagesParams, {
    skip: !userToken, // Skip if no userToken
    refetchOnMountOrArgChange: true,
  });

  // Handle image loading timeout - must come after imagesLoading is defined
  useEffect(() => {
    if (imagesLoading) {
      setImageLoadingTimeout(false);
      const timer = setTimeout(() => {
        setImageLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    } else {
      setImageLoadingTimeout(false);
    }
  }, [imagesLoading]);

  // Monitor authentication state changes - must come after all API hooks are defined
  useEffect(() => {
    if (userToken) {
      // Force refresh by updating refresh trigger to invalidate cache
      setRefreshTrigger(prev => prev + 1);
      // Also manually refetch to ensure immediate data loading
      refetchUserMoles();
      refetchMoleImages();
    }
  }, [userToken, refetchUserMoles, refetchMoleImages]);

  // Monitor language changes for body parts refresh
  useEffect(() => {
    // When language changes, the frontParams and backParams will change
    // and RTK Query will automatically refetch due to refetchOnMountOrArgChange: true
  }, [isPolish]);

  // Process data
  // Handle both flat structure and nested user_mole structure from API
  const userMoles = useMemo(() => {
    const results = userMolesData?.results || [];
    // If data has nested user_mole structure, flatten it by merging user_mole into main object
    return results.map((item) => {
      // If item has user_mole nested, merge it into the main object
      if (item.user_mole && typeof item.user_mole === 'object') {
        return {
          ...item,
          ...item.user_mole,
          // Keep original body_part if it exists at top level
          body_part: item.body_part || item.user_mole.body_part,
        };
      }
      return item;
    });
  }, [userMolesData?.results]);
  const allMoleImages = allMoleImagesData?.results || [];

  // Update selectedMoleImages when allMoleImages change and modal is open
  // This ensures the modal has the latest data after refresh
  useEffect(() => {
    if (showCarouselModal && selectedMole) {
      const updatedImages = allMoleImages.filter(
        (img) => img.user_mole?.uuid === selectedMole.uuid,
      );
      setSelectedMoleImages(updatedImages);
    }
  }, [allMoleImages, showCarouselModal, selectedMole]);

  // Combine body parts from both orientations
  const effectiveFrontBodyParts = frontBodyPartsData;
  const effectiveBackBodyParts = backBodyPartsData;

  // Group moles by body part for the body SVG (exclude unassigned moles)
  const molesByBodyPart = useMemo(() => {
    const grouped = {};
    userMoles
      .filter((mole) => mole.body_part && mole.body_part !== 'None' && mole.body_part !== null)
      .forEach((mole) => {
        if (!grouped[mole.body_part]) {
          grouped[mole.body_part] = [];
        }
        grouped[mole.body_part].push(mole);
      });
    return grouped;
  }, [userMoles]);

  // Get selected body part data
  const selectedBodyPartMoles = selectedBodyPart
    ? userMoles.filter((mole) => mole.body_part === selectedBodyPart)
    : [];

  // Get unassigned moles (no body part or null body part)
  const unassignedMoles = userMoles.filter(
    (mole) => !mole.body_part || mole.body_part === 'None' || mole.body_part === null,
  );

  const handleViewToggle = (newViewType) => {
    setViewType(newViewType);
    setSelectedBodyPart(null); // Reset selection when switching views
    setImageLoadingTimeout(false); // Reset timeout when switching views
  };

  // Helper function to get body part name
  const getDisplayBodyPartName = (bodyPartCode) => {
    const isLoading = frontBodyPartsLoading || backBodyPartsLoading;

    // Merge front/back body parts into a single dictionary of categories
    const allBodyParts = {};
    const mergeParts = (source) => {
      if (!source) return;
      Object.entries(source).forEach(([category, parts]) => {
        if (!Array.isArray(parts)) return;
        if (!allBodyParts[category]) allBodyParts[category] = [];
        const existingCodes = new Set(allBodyParts[category].map((p) => p.code));
        parts.forEach((p) => {
          if (p && p.code && !existingCodes.has(p.code)) {
            allBodyParts[category].push(p);
          }
        });
      });
    };
    mergeParts(frontBodyPartsData);
    mergeParts(backBodyPartsData);

    return getBodyPartName(bodyPartCode, allBodyParts, isLoading, i18n.language);
  };

  // More resilient loading state - don't block on images or body parts
  // Only show spinner if moles are loading AND we have no data yet AND not forced to render
  if (molesLoading && userMoles.length === 0 && !forceRender) {
    return (
      <Container fluid className='py-4'>
        <Row>
          <Col md={12} className='text-center py-5'>
            <Spinner animation='border' role='status'>
              <span className='visually-hidden'>{t('skinMoles.loading.loadingGeneric')}</span>
            </Spinner>
            <p className='mt-3'>{t('skinMoles.loading.loadingMoles')}</p>
            {/* Body parts loading text commented out for testing */}
            {false && (frontBodyPartsLoading || backBodyPartsLoading) && (
              <p className='mt-2'>
                <small className='text-muted'>{t('skinMoles.loading.loadingBodyParts')}</small>
              </p>
            )}
          </Col>
        </Row>
      </Container>
    );
  }

  // Show authentication loading state if userToken is not yet available
  if (!userToken) {
    return (
      <Container fluid className='py-4'>
        <Row>
          <Col md={12} className='text-center py-5'>
            <Spinner animation='border' role='status'>
              <span className='visually-hidden'>{t('skinMoles.loading.loadingGeneric')}</span>
            </Spinner>
            <p className='mt-3'>{t('skinMoles.loading.restoringAuth')}</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Error state with more details
  if (molesError) {
    return (
      <Container fluid className='py-4'>
        <Row>
          <Col md={12}>
            <Alert variant='danger'>
              <Alert.Heading>{t('skinMoles.errors.loadingMolesTitle')}</Alert.Heading>
              <p>{t('skinMoles.errors.loadingMolesMessage')}</p>
              <details>
                <summary>{t('skinMoles.errors.errorDetails')}</summary>
                <pre>{JSON.stringify(molesError, null, 2)}</pre>
              </details>
              <Button
                variant='outline-danger'
                onClick={() => window.location.reload()}
                className='mt-2'
              >
                {t('skinMoles.errors.refreshPage')}
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className='py-4'>
        <Row>
          <Col md={12}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <h2 className='mb-0'>{t('skinMoles.title')}</h2>
              {/* Body parts loading spinner commented out for testing */}
              {false && (frontBodyPartsLoading || backBodyPartsLoading) && (
                <div className='d-flex align-items-center'>
                  <Spinner animation='border' size='sm' className='me-2' />
                  <small className='text-muted'>{t('skinMoles.loading.loadingBodyParts')}</small>
                </div>
              )}
            </div>
            <div className='mb-3'>
              {isCreateMode ? (
                <div className='alert alert-success py-2 mb-1'>
                  <strong>{t('skinMoles.createMode.title')}</strong>
                  <br />
                  <small>{t('skinMoles.createMode.description')}</small>
                </div>
              ) : (
                <p className='text-muted mb-1'>
                  {t('skinMoles.trackedMolesInfo.youHave')} {userMoles.length}{' '}
                  {userMoles.length === 1
                    ? t('skinMoles.trackedMolesInfo.trackedMole')
                    : userMoles.length >= 5
                      ? t('skinMoles.trackedMolesInfo.trackedMolesPlural')
                      : t('skinMoles.trackedMolesInfo.trackedMoles')}
                  {molesLoading && ` (${t('skinMoles.trackedMolesInfo.loading')})`}
                </p>
              )}
              <div className='d-flex flex-wrap gap-2 align-items-center'>
                {unassignedMoles.length > 0 && (
                  <>
                    <Badge bg='warning' className='me-2'>
                      ⚠️ {unassignedMoles.length} {t('skinMoles.unassigned.count')}
                    </Badge>
                    <Button
                      variant='outline-warning'
                      size='sm'
                      onClick={() => setSelectedBodyPart('unassigned')}
                    >
                      {t('skinMoles.unassigned.viewButton')}
                    </Button>
                  </>
                )}
                {imagesError && (
                  <div className='d-flex align-items-center ms-2'>
                    <Badge bg='danger' className='me-2'>
                      📷 {t('skinMoles.errors.imageLoadError')}
                    </Badge>
                    <small className='text-muted'>{t('skinMoles.errors.imageLoadErrorDetails')}</small>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={4} className='mb-4'>
            <Card>
              <Card.Header>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h5 className='mb-0'>{t('skinMoles.bodyMap')}</h5>
                  <ButtonGroup size='sm'>
                    <Button
                      variant={viewType === 'front' ? 'primary' : 'outline-primary'}
                      onClick={() => handleViewToggle('front')}
                    >
                      {t('skinMoles.front')}
                    </Button>
                    <Button
                      variant={viewType === 'back' ? 'primary' : 'outline-primary'}
                      onClick={() => handleViewToggle('back')}
                    >
                      {t('skinMoles.back')}
                    </Button>
                  </ButtonGroup>
                </div>

                {/* Mode Toggle */}
                <div className='d-flex justify-content-between align-items-center'>
                  <small className='text-muted'>
                    {isCreateMode
                      ? t('skinMoles.createMode.bodyPartInstruction')
                      : t('skinMoles.clickToView')}
                  </small>
                  <Button
                    variant={isCreateMode ? 'success' : 'outline-success'}
                    size='sm'
                    onClick={handleToggleCreateMode}
                    className='d-flex align-items-center gap-1'
                  >
                    {isCreateMode
                      ? t('skinMoles.modeToggle.viewMode')
                      : t('skinMoles.modeToggle.createMode')}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className='text-center'>
                <InteractiveBodySVG
                  selectedBodyPart={selectedBodyPart}
                  onBodyPartClick={handleBodyPartCreateClick}
                  viewType={viewType}
                  molesData={userMoles}
                  isCreateMode={isCreateMode}
                />

                {/* Legend */}
                <div className='mt-3'>
                  <small className='d-block mb-1'>
                    <strong>{t('skinMoles.legend')}</strong>
                  </small>
                  <div className='d-flex justify-content-center flex-wrap gap-2'>
                    <div className='d-flex align-items-center'>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#e0e0e0',
                          marginRight: '4px',
                          border: '1px solid #999',
                        }}
                      ></div>
                      <small>0 {t('skinMoles.moles')}</small>
                    </div>
                    <div className='d-flex align-items-center'>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#90EE90',
                          marginRight: '4px',
                          border: '1px solid #999',
                        }}
                      ></div>
                      <small>1</small>
                    </div>
                    <div className='d-flex align-items-center'>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#FFD700',
                          marginRight: '4px',
                          border: '1px solid #999',
                        }}
                      ></div>
                      <small>2-3</small>
                    </div>
                    <div className='d-flex align-items-center'>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#FF6B6B',
                          marginRight: '4px',
                          border: '1px solid #999',
                        }}
                      ></div>
                      <small>4+</small>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card>
              <Card.Header>
                <h5 className='mb-0'>
                  {selectedBodyPart === 'unassigned'
                    ? (() => {
                        const count = unassignedMoles.length;
                        if (count === 1) {
                          return t('skinMoles.bodyPartSection.bodyPartTitle')
                            .replace('{{bodyPart}}', t('skinMoles.bodyPartSection.unassignedMoles'))
                            .replace('{{count}}', count)
                            .replace('{{mole}}', t('skinMoles.mole'));
                        } else if (count >= 5) {
                          return t('skinMoles.bodyPartSection.bodyPartTitlePlural2')
                            .replace('{{bodyPart}}', t('skinMoles.bodyPartSection.unassignedMoles'))
                            .replace('{{count}}', count)
                            .replace('{{moles2}}', t('skinMoles.molesPlural2'));
                        } else {
                          return t('skinMoles.bodyPartSection.bodyPartTitlePlural')
                            .replace('{{bodyPart}}', t('skinMoles.bodyPartSection.unassignedMoles'))
                            .replace('{{count}}', count)
                            .replace('{{moles}}', t('skinMoles.molesPlural'));
                        }
                      })()
                    : selectedBodyPart
                      ? (() => {
                          const count = selectedBodyPartMoles.length;
                          if (count === 1) {
                            return t('skinMoles.bodyPartSection.bodyPartTitle')
                              .replace('{{bodyPart}}', getDisplayBodyPartName(selectedBodyPart))
                              .replace('{{count}}', count)
                              .replace('{{mole}}', t('skinMoles.mole'));
                          } else if (count >= 5) {
                            return t('skinMoles.bodyPartSection.bodyPartTitlePlural2')
                              .replace('{{bodyPart}}', getDisplayBodyPartName(selectedBodyPart))
                              .replace('{{count}}', count)
                              .replace('{{moles2}}', t('skinMoles.molesPlural2'));
                          } else {
                            return t('skinMoles.bodyPartSection.bodyPartTitlePlural')
                              .replace('{{bodyPart}}', getDisplayBodyPartName(selectedBodyPart))
                              .replace('{{count}}', count)
                              .replace('{{moles}}', t('skinMoles.molesPlural'));
                          }
                        })()
                      : t('skinMoles.selectBodyPart')}
                </h5>
                {selectedBodyPart === 'unassigned' && (
                  <small className='text-warning'>
                    {t('skinMoles.bodyPartSection.unassignedMolesWarning')}
                  </small>
                )}
              </Card.Header>
              <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {selectedBodyPart ? (
                  (() => {
                    // Determine which moles to show
                    const molesToShow =
                      selectedBodyPart === 'unassigned' ? unassignedMoles : selectedBodyPartMoles;

                    // Only show loading spinner if there are moles to load images for
                    // AND we don't have image data yet AND we haven't timed out
                    const hasImageData = allMoleImages.length > 0;
                    const shouldShowImageLoading =
                      imagesLoading &&
                      molesToShow.length > 0 &&
                      !hasImageData &&
                      !imageLoadingTimeout;

                    if (shouldShowImageLoading) {
                      return (
                        <div className='text-center py-3'>
                          <Spinner animation='border' size='sm' />
                          <small className='ms-2'>
                            {t('skinMoles.bodyPartSection.loadingImages')}
                          </small>
                        </div>
                      );
                    }

                    // Show timeout warning but continue to show moles if images failed to load
                    const showTimeoutWarning =
                      imageLoadingTimeout && molesToShow.length > 0 && !hasImageData;

                    return molesToShow.length > 0 ? (
                      <>
                        {showTimeoutWarning && (
                          <div className='alert alert-warning py-2 mb-3'>
                            <small>{t('skinMoles.errors.imageLoadErrorDetails')}</small>
                          </div>
                        )}
                        {molesToShow.map((mole) => {
                          // Get images for this specific mole
                          const moleImages = allMoleImages.filter(
                            (img) => img.user_mole?.uuid === mole.uuid,
                          );

                          // Ensure moleImages is always an array
                          const finalMoleImages = Array.isArray(moleImages) ? moleImages : [];

                          // Debug logging removed - issues resolved

                          return (
                            <MoleCard
                              key={mole.uuid}
                              mole={mole}
                              moleImages={finalMoleImages}
                              isUnassigned={selectedBodyPart === 'unassigned'}
                              onShowCarousel={handleShowCarousel}
                              onMoleDeleted={handleMoleDeleted}
                            />
                          );
                        })}
                      </>
                    ) : (
                      <div className='text-center text-muted py-5'>
                        <p>
                          {selectedBodyPart === 'unassigned'
                            ? t('skinMoles.bodyPartSection.noUnassignedMolesFound')
                            : `${t('skinMoles.noMolesFound')} ${getDisplayBodyPartName(
                                selectedBodyPart,
                              )}`}
                        </p>
                      </div>
                    );
                  })()
                ) : (
                  <div className='text-center text-muted py-5'>
                    {isCreateMode ? (
                      <div>
                        <div className='mb-4'>
                          <i
                            className='bi bi-plus-circle'
                            style={{ fontSize: '3rem', color: '#28a745' }}
                          ></i>
                        </div>
                        <h5 className='text-success mb-3'>
                          {t('skinMoles.bodyPartSection.readyToAddNewMole')}
                        </h5>
                        <p>{t('skinMoles.bodyPartSection.clickOnBodyPartToCreate')}</p>
                        <div className='mt-4'>
                          <small className='text-muted'>
                            {t('skinMoles.bodyPartSection.bodyPartAutoSelectedTip')}
                          </small>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p>{t('skinMoles.clickOnLeft')}</p>
                        {unassignedMoles.length > 0 && (
                          <p className='mt-2'>
                            <Button
                              variant='warning'
                              size='sm'
                              onClick={() => setSelectedBodyPart('unassigned')}
                            >
                              {t('skinMoles.bodyPartSection.viewUnassignedMoles').replace(
                                '{{count}}',
                                unassignedMoles.length,
                              )}
                            </Button>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Image Carousel Modal */}
      <MoleImageCarouselModal
        show={showCarouselModal}
        onHide={handleHideCarousel}
        mole={selectedMole}
        moleImages={selectedMoleImages}
        translatedBodyPartName={selectedMoleBodyPartName}
        onMoleDeleted={handleMoleDeleted}
        onRefreshData={refetchMoleImages}
      />

      {/* Create Mole Modal */}
      <MoleCreationModal
        show={showCreateModal}
        onHide={handleHideCreateModal}
        selectedBodyPart={createBodyPart}
        bodyParts={{
          front: effectiveFrontBodyParts || {},
          back: effectiveBackBodyParts || {},
        }}
        bodyPartsLoading={frontBodyPartsLoading || backBodyPartsLoading}
        onClearBodyPart={handleClearCreateBodyPart}
        onMoleCreated={handleMoleCreated}
      />
    </>
  );
};

export default SkinMolesComponent;
